import { useState, useEffect, useRef } from 'react';
import { useSearchTargets } from '../hooks/useSearchTargets';

const COLOR_A = '#D75428';
const COLOR_B = '#2857D7';

export const SearchModal = ({ onSelect, onClose, label }) => {
  const COLOR = label === 'B' ? COLOR_B : COLOR_A;
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query), 300);
    return () => clearTimeout(timer);
  }, [query]);

  const { data, isLoading } = useSearchTargets(debouncedQuery);

  const targets = data ?? [];
  const groupResults = targets.filter((t) => t.type === 'group');
  const comedianResults = targets.filter((t) => t.type === 'comedian');

  const shownMemberNames = new Set(
    groupResults.flatMap((g) => g.members.map((m) => m.name))
  );
  const standaloneComedians = comedianResults.filter(
    (c) => !shownMemberNames.has(c.name)
  );

  useEffect(() => {
    setTimeout(() => inputRef.current?.focus(), 100);
  }, []);

  const handleSelect = (target) => {
    onSelect(target);
    onClose();
  };

  const handleMemberClick = (e, memberName) => {
    e.stopPropagation();
    const comedianTarget = targets.find(
      (t) => t.type === 'comedian' && t.name === memberName
    );
    if (comedianTarget) handleSelect(comedianTarget);
  };

  const hasResults = groupResults.length > 0 || standaloneComedians.length > 0;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 100,
        background: '#f7f7f7',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* ヘッダー */}
      <div
        style={{
          background: '#fff',
          borderBottom: `3px solid ${COLOR}`,
          padding: '12px 16px',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
        }}
      >
        <button
          onClick={onClose}
          style={{
            background: 'none',
            border: 'none',
            fontSize: 22,
            cursor: 'pointer',
            color: COLOR,
            padding: '4px 6px',
            lineHeight: 1,
            fontWeight: 'bold',
          }}
        >
          ←
        </button>
        <div style={{ flex: 1, position: 'relative' }}>
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="芸人またはグループを検索"
            style={{
              width: '100%',
              padding: '10px 36px 10px 14px',
              fontSize: 16,
              border: `1.5px solid ${COLOR}`,
              borderRadius: 8,
              boxSizing: 'border-box',
              outline: 'none',
              background: '#fff5f2',
            }}
          />
          {query && (
            <button
              onClick={() => setQuery('')}
              style={{
                position: 'absolute',
                right: 8,
                top: '50%',
                transform: 'translateY(-50%)',
                background: '#ccc',
                border: 'none',
                borderRadius: '50%',
                width: 20,
                height: 20,
                cursor: 'pointer',
                color: '#fff',
                fontSize: 11,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              ✕
            </button>
          )}
        </div>
      </div>

      {/* 検索結果 */}
      <div style={{ flex: 1, overflowY: 'auto' }}>
        {!query && (
          <p
            style={{
              padding: '32px 16px',
              color: '#aaa',
              fontSize: 14,
              textAlign: 'center',
            }}
          >
            芸人またはグループ名を入力してください
          </p>
        )}

        {isLoading && (
          <p
            style={{
              padding: '32px 16px',
              color: '#aaa',
              fontSize: 14,
              textAlign: 'center',
            }}
          >
            検索中...
          </p>
        )}

        {!isLoading && debouncedQuery && !hasResults && (
          <p
            style={{
              padding: '32px 16px',
              color: '#aaa',
              fontSize: 14,
              textAlign: 'center',
            }}
          >
            「{debouncedQuery}」に一致する結果はありません
          </p>
        )}

        {groupResults.map((group) => (
          <div key={group.id}>
            {/* グループ行 */}
            <div
              onClick={() => handleSelect(group)}
              style={{
                padding: '16px 16px',
                borderBottom: '1px solid #f0f0f0',
                background: '#fff',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 10,
              }}
            >
              <span style={{ fontSize: 16, fontWeight: 'bold', color: '#1a1a1a' }}>
                {group.name}
              </span>
            </div>
            {/* メンバー行 */}
            {group.members.map((m) => (
              <div
                key={m.name}
                onClick={(e) => handleMemberClick(e, m.name)}
                style={{
                  padding: '13px 16px 13px 42px',
                  borderBottom: '1px solid #f0f0f0',
                  fontSize: 15,
                  color: '#333',
                  cursor: 'pointer',
                  background: '#fafafa',
                }}
              >
                {m.name}
              </div>
            ))}
          </div>
        ))}

        {standaloneComedians.map((comedian) => (
          <div
            key={comedian.id}
            onClick={() => handleSelect(comedian)}
            style={{
              padding: '16px 16px',
              borderBottom: '1px solid #f0f0f0',
              background: '#fff',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <span style={{ fontSize: 15, color: '#1a1a1a', fontWeight: 'bold' }}>{comedian.name}</span>
            {comedian.groups.length > 0 && (
              <span style={{ fontSize: 12, color: '#aaa' }}>
                {comedian.groups[0].name}
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
